import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Search, Package, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

/**
 * ListingSearchInput — autocomplete search for "Frequently Bought Together" and linked listings
 *
 * value: array of { id, title } objects (or plain ID strings for backward compat)
 * onChange: called with the updated array
 * maxItems: optional — limits selection to N items (e.g. 1 for linked catering listing)
 * statusFilter: optional — array of allowed listing statuses (e.g. ['active','draft']).
 *   Omitted = no status filter, unchanged default behaviour for every existing caller.
 *   Catalog Architecture v7 Phase F1 — used by CatalogItemsCard's staged picker only.
 */
const ListingSearchInput = ({ value = [], onChange, placeholder = 'Search listings by name...', currentListingId, maxItems, statusFilter, helperText }) => {
  const { apiCall } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Memoize items to prevent infinite re-render loop
  const items = useMemo(() =>
    (Array.isArray(value) ? value : []).map(v =>
      typeof v === 'string' ? { id: v, title: v } : v
    ), [value]);

  // Use a ref for items inside the search callback to avoid stale closures
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 1) { setResults(prev => prev.length > 0 ? [] : prev); return; }
    setLoading(true);
    try {
      const statusParam = Array.isArray(statusFilter) && statusFilter.length > 0
        ? `&statuses=${encodeURIComponent(statusFilter.join(','))}`
        : '';
      const data = await apiCall(`/api/listings/search-autocomplete?q=${encodeURIComponent(q)}&limit=10${statusParam}`);
      // Filter out already-selected and the current listing itself
      const selectedIds = new Set(itemsRef.current.map(i => i.id));
      if (currentListingId) selectedIds.add(currentListingId);
      setResults((data || []).filter(r => !selectedIds.has(r.id)));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, currentListingId, statusFilter]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim()) {
      debounceRef.current = setTimeout(() => search(query), 250);
    } else {
      setResults(prev => prev.length > 0 ? [] : prev);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addItem = (listing) => {
    // Respect maxItems limit — replace if at limit (single-select behaviour when maxItems=1)
    const base = maxItems && items.length >= maxItems ? [] : items;
    const next = [...base, { id: listing.id, title: listing.title }];
    onChange(next);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const isAtLimit = maxItems && items.length >= maxItems;

  const removeItem = (id) => {
    onChange(items.filter(i => i.id !== id));
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="listing-search-input">
      {/* Selected items */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {items.map((item) => (
            <Badge
              key={item.id}
              variant="secondary"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200"
              data-testid={`selected-listing-${item.id}`}
            >
              <Package className="h-3 w-3 flex-shrink-0" />
              <span className="max-w-[200px] truncate text-sm">{item.title}</span>
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="ml-0.5 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                data-testid={`remove-listing-${item.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input — hidden when maxItems limit is reached */}
      {!isAtLimit && (
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query && setOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-8"
          data-testid="listing-search-query"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />}
      </div>
      )}

      {/* Dropdown results */}
      {open && (results.length > 0 || (query && !loading)) && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="listing-search-dropdown">
          {results.length > 0 ? results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => addItem(r)}
              className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 flex items-center justify-between gap-2 border-b last:border-b-0 transition-colors"
              data-testid={`listing-result-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm truncate">{r.title}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {r.price != null && (
                  <span className="text-xs text-gray-500">₹{r.price}</span>
                )}
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {r.listing_type || 'product'}
                </Badge>
              </div>
            </button>
          )) : (
            query && !loading && (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No listings found for "{query}"
              </div>
            )
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-1.5" data-testid="listing-search-helper-text">
        {helperText
          ? `${helperText}${items.length > 0 ? ` ${items.length} selected` : ''}`
          : maxItems === 1
            ? isAtLimit
              ? 'Click × to change the linked listing.'
              : 'Search and select one listing to link.'
            : `Search by listing name to add cross-sell recommendations.${items.length > 0 ? ` ${items.length} selected` : ''}`
        }
      </p>
    </div>
  );
};

export default ListingSearchInput;
